import { useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react'
import { hostError } from '../../api'
import { t } from '../../i18n'
import { Button, CodeCells, Dialog, Icon } from '../../ui'
import { one } from './nodeSwitch'

/*
 * ConfirmDialog, level 3 (operator console: + TOTP), for the node group actions
 * (GROUP-UI): 440 px, title a question naming the object, consequences as a
 * list with numbers, the code in six cells, focus on Cancel, Esc cancels and
 * focus goes back to the trigger. The action button repeats the object; it is
 * plain (not danger) where nothing on air breaks, danger where platforms
 * reconnect (moving a broadcast's outputs). A form (weight, settings, the
 * target and the typed word of a move) sits in the body above the code.
 */

const CODE_TRIES = 5
const empty = () => Array<string>(6).fill('')

export function GroupDialog({
  testId,
  title,
  facts,
  children,
  action,
  code = true,
  ready = true,
  returnFocus,
  onClose,
  submit,
  errorText,
  danger,
  primary,
  busyLabel,
}: {
  testId: string
  title: ReactNode
  facts?: ReactNode[]
  children?: ReactNode
  action: string
  /** false: the action needs no second factor (undrain, private address) */
  code?: boolean
  /** the form is valid */
  ready?: boolean
  returnFocus?: RefObject<HTMLElement | null>
  onClose: () => void
  /** resolves when done (the caller closes and says so); throws the center's refusal */
  submit: (totp?: string) => Promise<void>
  errorText: (e: unknown) => string
  /** the action breaks something on air (platforms reconnect): ConfirmDialog's danger button */
  danger?: boolean
  /** the main task of the view (create a group): the primary button */
  primary?: boolean
  /** the button's text while it runs (default "Saving…") */
  busyLabel?: string
}) {
  const id = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [cells, setCells] = useState(empty)
  const [busy, setBusy] = useState(false)
  const [tries, setTries] = useState(CODE_TRIES)
  const [codeError, setCodeError] = useState('')
  const [formError, setFormError] = useState('')
  const closeRef = useRef(onClose)
  const backRef = useRef(returnFocus)
  closeRef.current = onClose
  backRef.current = returnFocus

  useEffect(() => {
    cancelRef.current?.focus({ preventScroll: true }) // a form taller than a phone screen opens at its title
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
        return
      }
      if (e.key !== 'Tab') return
      const root = document.getElementById(`${id}-title`)?.closest('[role="alertdialog"]')
      if (!root) return
      const items = [...root.querySelectorAll<HTMLElement>('button, input, [tabindex="0"]')].filter((el) => !el.hasAttribute('disabled'))
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      backRef.current?.current?.focus()
    }
  }, [id])

  const codeReady = !code || cells.every((c) => c.length === 1)
  const run = async () => {
    if (!ready || !codeReady || busy || (code && tries <= 0)) return
    setBusy(true)
    setCodeError('')
    setFormError('')
    try {
      await submit(code ? cells.join('') : undefined)
    } catch (e) {
      const h = hostError(e)
      if (h?.code === 'totp_invalid') {
        const left = tries - 1
        setTries(left)
        setCells(empty())
        setCodeError(left > 0 ? t(one(left, 'node.wrongCode.one', 'node.wrongCode.many'), { n: left }) : t('node.locked'))
      } else if (h?.code === 'totp_locked') {
        setTries(0)
        setCodeError(h.retry_after_s ? t('node.lockedFor', { n: Math.ceil(h.retry_after_s / 60) }) : t('node.locked'))
      } else {
        setFormError(errorText(e))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      id={id}
      modal
      busy={busy}
      testId={`${testId}-dialog`}
      title={title}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" className="max-md:min-h-[44px]" data-testid={`${testId}-cancel`} onClick={onClose}>
            {t('node.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : primary ? 'primary' : 'default'} className="max-md:min-h-[44px]" busy={busy} disabled={!ready || !codeReady || (code && tries <= 0)} data-testid={`${testId}-confirm`} onClick={() => void run()}>
            {busy ? (busyLabel ?? t('group.busy')) : action}
          </Button>
        </>
      }
    >
      {facts && facts.length > 0 && (
        <ul data-testid={`${testId}-facts`}>
          {facts.map((f, i) => (
            <li key={i}>{f}</li>
          ))}
        </ul>
      )}
      {children}
      {code && <CodeCells id={`${testId}-code`} label={t('node.code')} value={cells} invalid={!!codeError} onChange={setCells} />}
      {codeError && (
        <span className="flex items-center gap-1.5 text-xs text-fail" role="alert" data-testid={`${testId}-code-error`}>
          <Icon name="state-fail" size={12} />
          {codeError}
        </span>
      )}
      {formError && (
        <p className="flex items-start gap-1.5 text-[13px] text-text" role="alert" data-testid={`${testId}-error`}>
          <Icon name="state-fail" size={16} className="mt-px flex-none text-fail" />
          <span>{formError}</span>
        </p>
      )}
    </Dialog>
  )
}
